import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Card,
  CardMedia,
  IconButton,
  CardContent,
  Avatar,
  Alert,
  Badge,
} from "@mui/material";
import { emphasize, styled } from "@mui/material/styles";
import HomeIcon from "@mui/icons-material/Home";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MdDelete, MdCloudUpload, MdAddCircle } from "react-icons/md";
import { IoMdCloudUpload } from "react-icons/io";
import { IoSearch } from "react-icons/io5";
import { FaCheckCircle, FaCalendarAlt } from "react-icons/fa";
import { BiPlay } from "react-icons/bi";
import { fetchDataFromApi, editData, uploadImage, deleteData } from "../../utils/api";
import { MyContext } from "../../App";
import { useNavigate, useParams } from "react-router-dom";

import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';



const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const backgroundColor =
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : theme.palette.grey[800];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    "&:hover, &:focus": {
      backgroundColor: emphasize(backgroundColor, 0.06),
    },
    "&:active": {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(backgroundColor, 0.12),
    },
  };
});

const EditOfferBanner = () => {
  const { id } = useParams();

  const [title, setTitle] = useState("");
  const [existingFiles, setExistingFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);

  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Date fields
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subCategoryFilter, setSubCategoryFilter] = useState("");

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  const context = useContext(MyContext);
  const navigate = useNavigate();

  const [showSelected, setShowSelected] = useState(true);

  const startDateRef = useRef(null);

  const endDateRef = useRef(null);



  useEffect(() => {
    fetchCategories();
    fetchOfferBanner();
  }, [id]);

  useEffect(() => {
    if (categoryFilter) {
      fetchSubCategories(categoryFilter);
    } else {
      setSubCategories([]);
      setSubCategoryFilter("");
    }
  }, [categoryFilter]);

  // Helper function to check if URL is video
  const isVideoUrl = (url) => {
    if (!url) return false;
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'];
    const extension = url.split('.').pop().toLowerCase().split('?')[0];
    return videoExtensions.includes(extension);
  };

  // Helper function to check if file is video
  const isVideoFile = (file) => {
    return file.type.startsWith('video/');
  };

  const fetchOfferBanner = async () => {
    setFetchLoading(true);
    try {
      const response = await fetchDataFromApi(`/api/homeOfferBanner/${id}`);
      if (response) {
        setTitle(response.title || "");
        setExistingFiles(response.images || []);
        setSelectedProducts(response.products || []);

        // Format dates for input fields
        if (response.startDate) {
          setStartDate(new Date(response.startDate).toISOString().split('T')[0]);
        }
        if (response.endDate) {
          setEndDate(new Date(response.endDate).toISOString().split('T')[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching offer banner:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to fetch offer banner",
      });
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetchDataFromApi("/api/category");
      if (response) {
        const categoryList = response.categoryList || response;
        setCategories(Array.isArray(categoryList) ? categoryList : []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  };

  const fetchSubCategories = async (catId) => {
    try {
      const response = await fetchDataFromApi(`/api/subCategory?categoryId=${catId}`);
      if (response) {
        const subCategoryList = response.subCategoryList || response;
        setSubCategories(Array.isArray(subCategoryList) ? subCategoryList : []);
      }
    } catch (error) {
      console.error("Error fetching sub categories:", error);
      setSubCategories([]);
    }
  };

  const searchProducts = async () => {
    if (!searchQuery && !brandFilter && !categoryFilter && !subCategoryFilter) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please enter at least one search criteria",
      });
      return;
    }

    setSearchLoading(true);
    try {
      const params = new URLSearchParams({
        ...(searchQuery && { search: searchQuery }),
        ...(brandFilter && { brand: brandFilter }),
        ...(categoryFilter && { category: categoryFilter }),
        ...(subCategoryFilter && { subCategory: subCategoryFilter }),
        limit: "2000",
      });

      const response = await fetchDataFromApi(
        `/api/homeOfferBanner/products/search?${params.toString()}`
      );

      if (response) {
        setProducts(Array.isArray(response) ? response : []);
      }
    } catch (error) {
      console.error("Error searching products:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to search products",
      });
      setProducts([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleNewFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    // Validate file types
    const validFiles = selectedFiles.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      return isImage || isVideo;
    });

    if (validFiles.length !== selectedFiles.length) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Only image and video files are allowed",
      });
    }

    setNewFiles((prev) => [...prev, ...validFiles]);

    const previews = validFiles.map((file) => ({
      url: URL.createObjectURL(file),
      type: file.type,
      isVideo: isVideoFile(file)
    }));

    setNewPreviews((prev) => [...prev, ...previews]);
  };

  const removeExistingFile = async (fileUrl, index) => {
    try {
      await deleteData(`/api/homeOfferBanner/deleteImage?img=${fileUrl}`);
      setExistingFiles((prev) => prev.filter((_, i) => i !== index));
      context.setAlertBox({
        open: true,
        error: false,
        msg: "File removed successfully",
      });
    } catch (error) {
      console.error("Error deleting file:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to delete file",
      });
    }
  };

  const removeNewFile = (index) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
    setNewPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleProductSelection = (product) => {
    setSelectedProducts((prev) => {
      const isSelected = prev.some((p) => p._id === product._id);
      if (isSelected) {
        return prev.filter((p) => p._id !== product._id);
      } else {
        return [...prev, product];
      }
    });
  };

  const isProductSelected = (productId) => {
    return selectedProducts.some((p) => p._id === productId);
  };

  const removeSelectedProduct = (productId) => {
    setSelectedProducts((prev) => prev.filter((p) => p._id !== productId));
  };

  const resetSearch = () => {
    setSearchQuery("");
    setBrandFilter("");
    setCategoryFilter("");
    setSubCategoryFilter("");
    setProducts([]);
  };

  const uploadNewFiles = async () => {
    if (newFiles.length === 0) return [];

    setUploading(true);
    try {
      const formData = new FormData();
      newFiles.forEach((file) => {
        formData.append("images", file);
      });

      const uploadedFiles = await uploadImage(
        "/api/homeOfferBanner/upload",
        formData
      );

      // Extract URLs from the response
      const fileUrls = uploadedFiles.map(item =>
        typeof item === 'string' ? item : item.url
      );

      return fileUrls;
    } catch (error) {
      console.error("Error uploading files:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to upload new files",
      });
      return [];
    } finally {
      setUploading(false);
    }
  };

  const validateDates = () => {
    if (endDate && startDate && new Date(endDate) < new Date(startDate)) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "End date cannot be before start date",
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please enter a title for the offer banner",
      });
      return;
    }

    if (selectedProducts.length === 0) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please select at least one product",
      });
      return;
    }

    if (existingFiles.length === 0 && newFiles.length === 0) {
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Please keep or add at least one image/video",
      });
      return;
    }

    if (!validateDates()) {
      return;
    }

    setLoading(true);

    try {
      const uploadedFiles = await uploadNewFiles();
      const allFiles = [...existingFiles, ...uploadedFiles];

      const dataToSubmit = {
        title: title.trim(),
        images: allFiles,
        products: selectedProducts.map((p) => p._id),
        startDate: startDate ? new Date(startDate).toISOString() : new Date().toISOString(),
        endDate: endDate ? new Date(endDate).toISOString() : null,
      };

      const response = await editData(`/api/homeOfferBanner/${id}`, dataToSubmit);

      if (response) {
        context.setAlertBox({
          open: true,
          error: false,
          msg: "Offer banner updated successfully",
        });
        navigate("/homeOfferBanners");
      }
    } catch (error) {
      console.error("Error updating offer banner:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Failed to update offer banner",
      });
    } finally {
      setLoading(false);
    }
  };

  const getDaysRemaining = () => {
    if (!endDate || !startDate) return null;
    const end = new Date(endDate);
    const start = new Date(startDate);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return days;
  };

  if (fetchLoading) {
    return (
      <div className="right-content w-100">
        <Box className="text-center p-5">
          <CircularProgress />
          <Typography className="mt-3">Loading offer banner...</Typography>
        </Box>
      </div>
    );
  }

  return (
    <div className="right-content w-100">
      <div className="card shadow border-0 w-100 flex-row p-4 align-items-center">
        <h5 className="mb-0">Edit Offer Banner</h5>

        <Breadcrumbs aria-label="breadcrumb" className="ml-auto breadcrumbs_">
          <StyledBreadcrumb
            component="a"
            href="/"
            label="Dashboard"
            icon={<HomeIcon fontSize="small" />}
          />
          <StyledBreadcrumb
            component="a"
            href="/homeOfferBanners"
            label="Offer Banners"
          />
          <StyledBreadcrumb label="Edit" deleteIcon={<ExpandMoreIcon />} />
        </Breadcrumbs>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Title Section */}
        <Box
          sx={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
            padding: '32px',
            marginTop: '32px',
            border: '1px solid #f0f0f0',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              marginBottom: '24px',
              fontWeight: 600,
              color: '#263238',
              fontSize: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              paddingBottom: '12px',
              borderBottom: '2px solid #e0e0e0',
            }}
          >
            Offer Title *
          </Typography>

          <TextField
            fullWidth
            label="Enter Offer Title"
            placeholder="e.g., Weekend Super Sale, Flash Deal, Mega Discount..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            helperText="This title will be displayed on the banner slider"
            inputProps={{
              maxLength: 100,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                fontSize: '1rem',
                '& fieldset': {
                  borderWidth: '2px',
                  borderColor: '#e0e0e0',
                  transition: 'border-color 0.3s ease',
                },
                '&:hover': {
                  background: '#ffffff',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
                  '& fieldset': {
                    borderColor: '#1976d2',
                  },
                },
                '&.Mui-focused': {
                  background: '#ffffff',
                  boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
                  '& fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: '2px',
                  },
                },
              },
              '& .MuiInputLabel-root': {
                fontWeight: 600,
                color: '#616161',
                fontSize: '1rem',
                '&.Mui-focused': {
                  color: '#1976d2',
                  fontWeight: 700,
                },
              },
              '& .MuiInputBase-input': {
                padding: '16px 18px',
                fontSize: '1rem',
                fontWeight: 500,
                color: '#263238',
                lineHeight: 1.6,
                '&::placeholder': {
                  color: '#9e9e9e',
                  opacity: 0.8,
                  fontStyle: 'italic',
                  fontSize: '0.95rem',
                },
              },
              '& .MuiFormHelperText-root': {
                marginTop: '10px',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#757575',
                fontStyle: 'italic',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                '&::before': {
                  content: '"ℹ️"',
                  fontSize: '14px',
                },
              },
            }}
          />

          {title && (
            <Typography
              variant="caption"
              sx={{
                marginTop: '12px',
                display: 'block',
                fontSize: '0.85rem',
                fontWeight: 600,
                color: title.length >= 90 ? '#f44336' : title.length >= 70 ? '#ff9800' : '#4caf50',
                textAlign: 'right',
                animation: 'fadeIn 0.3s ease-in',
                '@keyframes fadeIn': {
                  from: { opacity: 0, transform: 'translateY(-5px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
                padding: '8px 12px',
                background: title.length >= 90
                  ? 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)'
                  : title.length >= 70
                    ? 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)'
                    : 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                borderRadius: '8px',
                border: `2px solid ${title.length >= 90 ? '#f44336' : title.length >= 70 ? '#ff9800' : '#4caf50'}`,
                boxShadow: title.length >= 90
                  ? '0 2px 8px rgba(244, 67, 54, 0.2)'
                  : title.length >= 70
                    ? '0 2px 8px rgba(255, 152, 0, 0.2)'
                    : '0 2px 8px rgba(76, 175, 80, 0.2)',
                transition: 'all 0.3s ease',
                position: 'relative',
                '&::before': {
                  content: title.length >= 90 ? '"⚠️"' : title.length >= 70 ? '"⚡"' : '"✓"',
                  position: 'absolute',
                  left: '12px',
                  fontSize: '16px',
                },
                paddingLeft: '36px',
              }}
            >
              Character count: {title.length}/100
              {title.length >= 90 && ' - Approaching limit!'}
              {title.length >= 70 && title.length < 90 && ' - Keep it concise'}
            </Typography>
          )}
        </Box>

        {/* Date Selection Section */}
        <Box
          sx={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
            padding: '32px',
            marginTop: '32px',
            border: '1px solid #f0f0f0',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              marginBottom: '28px',
              fontWeight: 600,
              color: '#263238',
              fontSize: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              paddingBottom: '16px',
              borderBottom: '2px solid #e0e0e0',
            }}
          >
            <FaCalendarAlt
              style={{
                verticalAlign: 'middle',
                fontSize: '22px',
                color: '#1976d2',
              }}
            />
            Offer Duration *
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                inputRef={startDateRef}
                onClick={() => startDateRef.current?.showPicker()}
                InputLabelProps={{
                  shrink: true,
                }}
                required
                helperText="When should this offer start?"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& fieldset': {
                      borderWidth: '2px',
                      borderColor: '#e0e0e0',
                      transition: 'border-color 0.3s ease',
                    },
                    '&:hover': {
                      background: '#ffffff',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.15)',
                      '& fieldset': {
                        borderColor: '#4caf50',
                      },
                    },
                    '&.Mui-focused': {
                      background: '#ffffff',
                      boxShadow: '0 0 0 3px rgba(76, 175, 80, 0.1)',
                      '& fieldset': {
                        borderColor: '#4caf50',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 600,
                    color: '#616161',
                    fontSize: '0.95rem',
                    '&.Mui-focused': {
                      color: '#4caf50',
                      fontWeight: 700,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    marginTop: '8px',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: '#757575',
                    fontStyle: 'italic',
                  },
                  '& .MuiInputBase-input': {
                    padding: '14px 16px',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    color: '#263238',
                    cursor: 'pointer',
                    '&::-webkit-calendar-picker-indicator': {
                      cursor: 'pointer',
                      fontSize: '18px',
                      opacity: 0.6,
                      transition: 'opacity 0.3s ease, transform 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        transform: 'scale(1.15)',
                      },
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
  type="date"
  label="End Date (Optional)"
  value={endDate}
  onChange={(e) => setEndDate(e.target.value)}
  inputRef={endDateRef}
  onClick={() => endDateRef.current?.showPicker()}
  InputLabelProps={{
    shrink: true,
  }}
  inputProps={{
    min: startDate,
  }}
  helperText="Leave empty for no end date"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& fieldset': {
                      borderWidth: '2px',
                      borderColor: '#e0e0e0',
                      transition: 'border-color 0.3s ease',
                    },
                    '&:hover': {
                      background: '#ffffff',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)',
                      '& fieldset': {
                        borderColor: '#f44336',
                      },
                    },
                    '&.Mui-focused': {
                      background: '#ffffff',
                      boxShadow: '0 0 0 3px rgba(244, 67, 54, 0.1)',
                      '& fieldset': {
                        borderColor: '#f44336',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 600,
                    color: '#616161',
                    fontSize: '0.95rem',
                    '&.Mui-focused': {
                      color: '#f44336',
                      fontWeight: 700,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    marginTop: '8px',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: '#757575',
                    fontStyle: 'italic',
                  },
                  '& .MuiInputBase-input': {
                    padding: '14px 16px',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    color: '#263238',
                    cursor: 'pointer',
                    '&::-webkit-calendar-picker-indicator': {
                      cursor: 'pointer',
                      fontSize: '18px',
                      opacity: 0.6,
                      transition: 'opacity 0.3s ease, transform 0.3s ease',
                      '&:hover': {
                        opacity: 1,
                        transform: 'scale(1.15)',
                      },
                    },
                  },
                }}
              />
            </Grid>
          </Grid>

          {endDate && getDaysRemaining() !== null && (
            <Alert
              severity="info"
              sx={{
                marginTop: '24px',
                borderRadius: '12px',
                border: '2px solid #2196f3',
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                boxShadow: '0 4px 16px rgba(33, 150, 243, 0.2)',
                animation: 'slideDown 0.5s ease-out',
                '@keyframes slideDown': {
                  from: {
                    opacity: 0,
                    transform: 'translateY(-20px)',
                  },
                  to: {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
                '& .MuiAlert-icon': {
                  fontSize: '28px',
                  color: '#1976d2',
                  animation: 'bounce 1s ease-in-out infinite',
                  '@keyframes bounce': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(-5px)' },
                  },
                },
                '& .MuiAlert-message': {
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  color: '#0d47a1',
                  lineHeight: 1.6,
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 0.5,
                },
                '& strong': {
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: '#1976d2',
                  padding: '2px 8px',
                  background: 'rgba(255, 255, 255, 0.7)',
                  borderRadius: '6px',
                  border: '1px solid #90caf9',
                },
              }}
            >
              This offer will run for <strong>{getDaysRemaining()}</strong> day(s)
              {getDaysRemaining() > 0
                ? ` (${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()})`
                : " (Please check your dates)"}
            </Alert>
          )}
        </Box>

        {/* Banner Images/Videos Section */}
        <Box
          sx={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
            padding: '32px',
            marginTop: '32px',
            border: '1px solid #f0f0f0',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              marginBottom: '32px',
              fontWeight: 600,
              color: '#263238',
              fontSize: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              paddingBottom: '12px',
              borderBottom: '2px solid #e0e0e0',
            }}
          >
            Banner Images/Videos *
            <Chip
              label="Auto-compressed"
              size="small"
              color="success"
              sx={{ fontSize: '0.7rem', fontWeight: 600 }}
            />
          </Typography>

          {/* Existing Files Section */}
          <Typography
            variant="subtitle2"
            sx={{
              marginBottom: '16px',
              fontWeight: 600,
              color: '#424242',
              fontSize: '0.95rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            📁 Existing Files
          </Typography>

          {existingFiles.length > 0 ? (
            <Grid
              container
              spacing={2.5}
              sx={{
                marginBottom: '32px',
                animation: 'fadeIn 0.5s ease-in',
                '@keyframes fadeIn': {
                  from: { opacity: 0, transform: 'translateY(10px)' },
                  to: { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              {existingFiles.map((file, index) => {
                const isVideo = isVideoUrl(file);
                return (
                  <Grid item xs={6} sm={4} md={3} key={index}>
                    <Card
                      sx={{
                        position: 'relative',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        border: '2px solid #e0e0e0',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        '&:hover': {
                          transform: 'translateY(-8px) scale(1.02)',
                          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.15)',
                          borderColor: '#ff9800',
                          '& .delete-overlay': {
                            opacity: 1,
                          },
                          '& .MuiCardMedia-root, & video': {
                            transform: 'scale(1.1)',
                            filter: 'brightness(0.85)',
                          },
                        },
                      }}
                    >
                      {/* Existing Badge */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          backgroundColor: 'rgba(255, 152, 0, 0.95)',
                          backdropFilter: 'blur(10px)',
                          color: '#ffffff',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          zIndex: 2,
                          boxShadow: '0 2px 8px rgba(255, 152, 0, 0.4)',
                        }}
                      >
                        Existing
                      </Box>

                      {/* Preview - Image or Video */}
                      {isVideo ? (
                        <Box sx={{ position: 'relative', height: 180 }}>
                          <video
                            src={file}
                            style={{
                              width: '100%',
                              height: '180px',
                              objectFit: 'cover',
                              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              background: 'rgba(0, 0, 0, 0.6)',
                              borderRadius: '50%',
                              width: '50px',
                              height: '50px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <BiPlay size={32} color="#ffffff" />
                          </Box>
                        </Box>
                      ) : (
                        <CardMedia
                          component="img"
                          height="180"
                          image={file}
                          sx={{
                            objectFit: 'cover',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />
                      )}

                      {/* Delete Overlay */}
                      <Box
                        className="delete-overlay"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(135deg, rgba(211, 47, 47, 0.85) 0%, rgba(198, 40, 40, 0.9) 100%)',
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          pointerEvents: 'none',
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#ffffff',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                          }}
                        >
                          Remove {isVideo ? 'Video' : 'Image'}
                        </Typography>
                      </Box>

                      {/* Delete Button */}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeExistingFile(file, index)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          backdropFilter: 'blur(10px)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          zIndex: 2,
                          '&:hover': {
                            backgroundColor: '#ffffff',
                            transform: 'scale(1.15) rotate(90deg)',
                            boxShadow: '0 6px 20px rgba(211, 47, 47, 0.4)',
                          },
                          '&:active': {
                            transform: 'scale(1.05) rotate(90deg)',
                          },
                        }}
                      >
                        <MdDelete size={20} />
                      </IconButton>

                      {/* File Type & Index Badge */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          backgroundColor: isVideo ? 'rgba(156, 39, 176, 0.9)' : 'rgba(0, 0, 0, 0.75)',
                          backdropFilter: 'blur(10px)',
                          color: '#ffffff',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          zIndex: 1,
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        {isVideo ? '🎥' : '🖼️'} {index + 1} / {existingFiles.length}
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Alert
              severity="warning"
              sx={{
                marginBottom: '32px',
                borderRadius: '10px',
                border: '2px solid #ff9800',
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                boxShadow: '0 2px 8px rgba(255, 152, 0, 0.15)',
                '& .MuiAlert-icon': {
                  fontSize: '28px',
                },
                '& .MuiAlert-message': {
                  fontWeight: 500,
                  fontSize: '0.95rem',
                },
              }}
            >
              No existing files. Please add new images/videos below.
            </Alert>
          )}

          {/* Add New Files Section */}
          <Typography
            variant="subtitle2"
            sx={{
              marginBottom: '16px',
              fontWeight: 600,
              color: '#424242',
              fontSize: '0.95rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              paddingTop: '16px',
            }}
          >
            ➕ Add New Images/Videos
          </Typography>

          {/* Upload Box */}
          <Box
            sx={{
              border: '2px dashed #bdbdbd',
              borderRadius: '12px',
              padding: '32px',
              textAlign: 'center',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                borderColor: '#4caf50',
                background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(76, 175, 80, 0.15)',
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                transition: 'left 0.5s',
              },
              '&:hover::before': {
                left: '100%',
              },
            }}
          >
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleNewFileChange}
              style={{ display: "none" }}
              id="new-file-upload"
            />
            <label htmlFor="new-file-upload" style={{ cursor: 'pointer', width: '100%' }}>
              <Button
                variant="outlined"
                component="span"
                startIcon={<IoMdCloudUpload />}
                fullWidth
                sx={{
                  height: '140px',
                  fontSize: '1.1rem',
                  border: 'none',
                  background: 'transparent',
                  color: '#616161',
                  textTransform: 'none',
                  flexDirection: 'column',
                  gap: 1.5,
                  padding: '20px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'transparent',
                    border: 'none',
                    color: '#4caf50',
                    '& .upload-icon': {
                      transform: 'scale(1.15) translateY(-5px)',
                      color: '#4caf50',
                    },
                  },
                }}
              >
                <Box>
                  <IoMdCloudUpload
                    size={48}
                    className="upload-icon"
                    style={{
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      color: '#757575',
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      marginTop: '12px',
                      color: 'inherit',
                      letterSpacing: '0.3px',
                    }}
                  >
                    Click to Add More Images or Videos
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.875rem',
                      color: '#9e9e9e',
                      display: 'block',
                      marginTop: '8px',
                      fontWeight: 500,
                    }}
                  >
                    Support: JPG, PNG, WEBP, MP4, AVI, MOV (Max 50MB each)
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.75rem',
                      color: '#4caf50',
                      display: 'block',
                      marginTop: '4px',
                      fontWeight: 600,
                    }}
                  >
                    📦 Files will be automatically compressed before upload
                  </Typography>
                </Box>
              </Button>
            </label>
          </Box>

          {/* New File Previews */}
          {newPreviews.length > 0 && (
            <Grid
              container
              spacing={2.5}
              sx={{
                marginTop: '24px',
                animation: 'slideIn 0.5s ease-out',
                '@keyframes slideIn': {
                  from: { opacity: 0, transform: 'translateX(-20px)' },
                  to: { opacity: 1, transform: 'translateX(0)' },
                },
              }}
            >
              {newPreviews.map((preview, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Card
                    sx={{
                      position: 'relative',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '2px solid #4caf50',
                      boxShadow: '0 4px 16px rgba(76, 175, 80, 0.2)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f1f8f4 100%)',
                      '&:hover': {
                        transform: 'translateY(-8px) scale(1.02)',
                        boxShadow: '0 12px 28px rgba(76, 175, 80, 0.3)',
                        borderColor: '#2e7d32',
                        '& .delete-overlay': {
                          opacity: 1,
                        },
                        '& .MuiCardMedia-root, & video': {
                          transform: 'scale(1.1)',
                          filter: 'brightness(0.85)',
                        },
                        '& .new-badge': {
                          transform: 'scale(1.1)',
                          boxShadow: '0 4px 16px rgba(76, 175, 80, 0.5)',
                        },
                      },
                    }}
                  >
                    {/* New Badge with Animation */}
                    <Chip
                      label="NEW"
                      color="success"
                      size="small"
                      className="new-badge"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                        zIndex: 2,
                        boxShadow: '0 2px 8px rgba(76, 175, 80, 0.4)',
                        animation: 'pulse 2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': {
                            transform: 'scale(1)',
                            boxShadow: '0 2px 8px rgba(76, 175, 80, 0.4)',
                          },
                          '50%': {
                            transform: 'scale(1.05)',
                            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.6)',
                          },
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    />

                    {/* Preview - Image or Video */}
                    {preview.isVideo ? (
                      <Box sx={{ position: 'relative', height: 180 }}>
                        <video
                          src={preview.url}
                          style={{
                            width: '100%',
                            height: '180px',
                            objectFit: 'cover',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            background: 'rgba(0, 0, 0, 0.6)',
                            borderRadius: '50%',
                            width: '50px',
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <BiPlay size={32} color="#ffffff" />
                        </Box>
                      </Box>
                    ) : (
                      <CardMedia
                        component="img"
                        height="180"
                        image={preview.url}
                        sx={{
                          objectFit: 'cover',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      />
                    )}

                    {/* Delete Overlay */}
                    <Box
                      className="delete-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(211, 47, 47, 0.85) 0%, rgba(198, 40, 40, 0.9) 100%)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                        }}
                      >
                        Remove {preview.isVideo ? 'Video' : 'Image'}
                      </Typography>
                    </Box>

                    {/* Delete Button */}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeNewFile(index)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        zIndex: 2,
                        '&:hover': {
                          backgroundColor: '#ffffff',
                          transform: 'scale(1.15) rotate(90deg)',
                          boxShadow: '0 6px 20px rgba(211, 47, 47, 0.4)',
                        },
                        '&:active': {
                          transform: 'scale(1.05) rotate(90deg)',
                        },
                      }}
                    >
                      <MdDelete size={20} />
                    </IconButton>

                    {/* File Type & Index Badge */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        background: preview.isVideo
                          ? 'linear-gradient(135deg, #6a1b9a 0%, #9c27b0 100%)'
                          : 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
                        backdropFilter: 'blur(10px)',
                        color: '#ffffff',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        zIndex: 1,
                        boxShadow: preview.isVideo
                          ? '0 2px 8px rgba(156, 39, 176, 0.5)'
                          : '0 2px 8px rgba(46, 125, 50, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      {preview.isVideo ? '🎥' : '🖼️'} {index + 1} / {newPreviews.length}
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Product Search Section */}
        <Box className="card shadow border-0 p-4 mt-4">
          <Typography variant="h6" className="mb-3">
            Search & Select Products *
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Product Name"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && searchProducts()}
                InputProps={{
                  startAdornment: <IoSearch className="mr-2" />,
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Brand"
                placeholder="Enter brand..."
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {Array.isArray(categories) && categories.map((cat) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small" disabled={!categoryFilter}>
                <InputLabel>Sub Category</InputLabel>
                <Select
                  value={subCategoryFilter}
                  label="Sub Category"
                  onChange={(e) => setSubCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {Array.isArray(subCategories) && subCategories.map((subCat) => (
                    <MenuItem key={subCat._id} value={subCat._id}>
                      {subCat.subCat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'stretch'
                }}
              >
                <Button
                  variant="contained"
                  onClick={searchProducts}
                  disabled={searchLoading}
                  startIcon={searchLoading ? <CircularProgress size={16} /> : <IoSearch />}
                  fullWidth
                  sx={{
                    flex: 2,
                    background: searchLoading
                      ? 'linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)'
                      : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    borderRadius: '10px',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    padding: '10px 24px',
                    boxShadow: searchLoading
                      ? '0 2px 8px rgba(25, 118, 210, 0.2)'
                      : '0 4px 14px rgba(25, 118, 210, 0.35)',
                    letterSpacing: '0.3px',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: 0,
                      height: 0,
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.3)',
                      transform: 'translate(-50%, -50%)',
                      transition: 'width 0.6s, height 0.6s',
                    },
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                      boxShadow: '0 6px 20px rgba(25, 118, 210, 0.45)',
                      transform: 'translateY(-3px)',
                      '&::before': {
                        width: '300px',
                        height: '300px',
                      },
                    },
                    '&:active': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 3px 10px rgba(25, 118, 210, 0.3)',
                    },
                    '&:disabled': {
                      background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                      color: '#9e9e9e',
                      boxShadow: 'none',
                    },
                    '& .MuiButton-startIcon': {
                      marginRight: '8px',
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover .MuiButton-startIcon': {
                      transform: 'scale(1.1) rotate(5deg)',
                    },
                    '&:focus-visible': {
                      outline: '3px solid rgba(25, 118, 210, 0.5)',
                      outlineOffset: '4px',
                    },
                  }}
                >
                  {searchLoading ? "Searching..." : "Search"}
                </Button>

                <Button
                  variant="outlined"
                  onClick={resetSearch}
                  sx={{
                    flex: 1,
                    minWidth: '80px',
                    border: '2px solid #d32f2f',
                    color: '#d32f2f',
                    borderRadius: '10px',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    textTransform: 'none',
                    padding: '10px 20px',
                    background: '#ffffff',
                    letterSpacing: '0.3px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                      borderColor: '#c62828',
                      borderWidth: '2px',
                      color: '#c62828',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 4px 14px rgba(211, 47, 47, 0.25)',
                    },
                    '&:active': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(211, 47, 47, 0.2)',
                    },
                    '& .MuiButton-startIcon': {
                      marginRight: '8px',
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover .MuiButton-startIcon': {
                      transform: 'rotate(-180deg)',
                    },
                    '&:focus-visible': {
                      outline: '3px solid rgba(211, 47, 47, 0.5)',
                      outlineOffset: '4px',
                    },
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Selected Products Display */}
        {selectedProducts.length > 0 && (
          <Box
            sx={{
              background: 'linear-gradient(135deg, #e3f2fd 0%, #f5f9ff 100%)',
              borderRadius: '12px',
              boxShadow: '0 4px 16px rgba(25, 118, 210, 0.15)',
              padding: '32px',
              marginTop: '32px',
              border: '2px solid #1976d2',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #1976d2, #42a5f5, #1976d2)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 3s linear infinite',
              },
              '@keyframes shimmer': {
                '0%': { backgroundPosition: '-200% 0' },
                '100%': { backgroundPosition: '200% 0' },
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#263238',
                  fontSize: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&::before': {
                    content: '"✓"',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                    borderRadius: '50%',
                    color: '#ffffff',
                    fontSize: '18px',
                    fontWeight: 700,
                    boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                  },
                }}
              >
                Selected Products ({selectedProducts.length})
              </Typography>

              {/* RIGHT SIDE BUTTONS */}
              <Box sx={{ display: 'flex', gap: 1 }}>

                {/* Toggle Button */}
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setShowSelected(!showSelected)}
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    background: 'linear-gradient(135deg, #2073b8 0%, #0eb4b9 100%)',
                  }}
                >
                  {showSelected ? 'Hide' : 'Show'}
                  {showSelected ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </Button>

                {/* Clear All */}
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => setSelectedProducts([])}
                  sx={{
                    borderRadius: '20px',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    textTransform: 'none',
                    padding: '6px 20px',
                    borderWidth: '2px',
                    '&:hover': {
                      borderWidth: '2px',
                      background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  Clear All
                </Button>

              </Box>
            </Box>

            <Collapse in={showSelected} timeout={600}>
              <Grid container spacing={2}>
                {selectedProducts.map((product) => (
                  <Grid item xs={12} sm={6} md={4} key={product._id}>
                    <Card
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: '16px',
                        background: '#ffffff',
                        borderRadius: '10px',
                        border: '1px solid #bbdefb',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.1)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateX(5px)',
                          boxShadow: '0 4px 16px rgba(25, 118, 210, 0.2)',
                          borderColor: '#1976d2',
                          '& .product-avatar': {
                            transform: 'scale(1.1) rotate(5deg)',
                          },
                          '& .delete-button': {
                            transform: 'scale(1.15) rotate(90deg)',
                            background: '#ffebee',
                          },
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          background: 'linear-gradient(180deg, #1976d2 0%, #42a5f5 100%)',
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                        },
                        '&:hover::before': {
                          opacity: 1,
                        },
                      }}
                    >
                      <Avatar
                        src={product.images?.[0]}
                        variant="rounded"
                        className="product-avatar"
                        sx={{
                          width: 60,
                          height: 60,
                          marginRight: '16px',
                          border: '2px solid #e3f2fd',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                          transition: 'transform 0.3s ease',
                        }}
                      />

                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            color: '#263238',
                            lineHeight: 1.4,
                            marginBottom: '4px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {product.name}
                        </Typography>

                        {product.brand && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#757575',
                              fontSize: '0.8rem',
                              marginBottom: '4px',
                              fontWeight: 500,
                            }}
                          >
                            Brand: {product.brand}
                          </Typography>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#1976d2',
                              fontSize: '0.95rem',
                              fontWeight: 700,
                            }}
                          >
                            ₹{product.price}
                          </Typography>

                          {/* OUT OF STOCK BADGE */}
                          {product.isOutOfStock && (
                            <Chip
                              label="OUT OF STOCK"
                              size="small"
                              sx={{
                                height: '18px',
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                backgroundColor: '#ff5252',
                                color: '#ffffff',
                                animation: 'pulse 2s ease-in-out infinite',
                                '@keyframes pulse': {
                                  '0%, 100%': { opacity: 1 },
                                  '50%': { opacity: 0.7 },
                                },
                              }}
                            />
                          )}
                        </Box>
                      </Box>

                      <IconButton
                        size="small"
                        color="error"
                        className="delete-button"
                        onClick={() => removeSelectedProduct(product._id)}
                        sx={{
                          marginLeft: '8px',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          background: 'rgba(244, 67, 54, 0.05)',
                          '&:hover': {
                            background: '#ffebee',
                          },
                        }}
                      >
                        <MdDelete size={20} />
                      </IconButton>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Collapse>

          </Box>
        )}

        {/* Product Search Results */}
        {products.length > 0 && (
          <Box
            sx={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
              padding: '32px',
              marginTop: '32px',
              border: '1px solid #f0f0f0',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                marginBottom: '24px',
                fontWeight: 600,
                color: '#263238',
                fontSize: '1.25rem',
                paddingBottom: '12px',
                borderBottom: '2px solid #e0e0e0',
              }}
            >
              Search Results ({products.length} products found)
            </Typography>

            <Grid container spacing={2}>
              {products.map((product) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={product._id}>
                  <Card
                    className={`product-search-card ${isProductSelected(product._id) ? "selected" : ""}`}
                    sx={{
                      border: isProductSelected(product._id)
                        ? "2px solid #1976d2"
                        : "1px solid #e0e0e0",
                      cursor: "pointer",
                      position: "relative",
                      borderRadius: '10px',
                      height: '100%',
                      maxHeight: '460px',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      background: isProductSelected(product._id)
                        ? 'linear-gradient(135deg, #e3f2fd 0%, #f5f9ff 100%)'
                        : '#ffffff',
                      '&:hover': {
                        transform: 'translateY(-6px) scale(1.02)',
                        boxShadow: isProductSelected(product._id)
                          ? '0 12px 28px rgba(25, 118, 210, 0.25)'
                          : '0 8px 24px rgba(0, 0, 0, 0.12)',
                        borderColor: isProductSelected(product._id) ? '#1565c0' : '#bdbdbd',
                        '& .product-image': {
                          transform: 'scale(1.08)',
                        },
                      },
                    }}
                    onClick={() => toggleProductSelection(product)}
                  >
                    {isProductSelected(product._id) && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          zIndex: 2,
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          borderRadius: "50%",
                          padding: '2px',
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                          animation: 'checkBounce 0.4s ease',
                          '@keyframes checkBounce': {
                            '0%': { transform: 'scale(0)', opacity: 0 },
                            '50%': { transform: 'scale(1.2)' },
                            '100%': { transform: 'scale(1)', opacity: 1 },
                          },
                        }}
                      >
                        <FaCheckCircle size={24} color="#1976d2" />
                      </Box>
                    )}

                    {/* OUT OF STOCK BADGE ON IMAGE */}
                    {product.isOutOfStock && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          zIndex: 2,
                          backgroundColor: 'rgba(244, 67, 54, 0.95)',
                          color: '#ffffff',
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          letterSpacing: '0.5px',
                          boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)',
                          animation: 'outOfStockPulse 2s ease-in-out infinite',
                          '@keyframes outOfStockPulse': {
                            '0%, 100%': {
                              transform: 'scale(1)',
                              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)',
                            },
                            '50%': {
                              transform: 'scale(1.05)',
                              boxShadow: '0 6px 16px rgba(244, 67, 54, 0.6)',
                            },
                          },
                        }}
                      >
                        OUT OF STOCK
                      </Box>
                    )}

                    <CardMedia
                      component="img"
                      height="160"
                      image={product.images?.[0] || "/placeholder.jpg"}
                      alt={product.name}
                      className="product-image"
                      sx={{
                        objectFit: "cover",
                        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        borderBottom: '1px solid #f5f5f5',
                        filter: product.isOutOfStock ? 'grayscale(50%) opacity(0.7)' : 'none',
                      }}
                    />

                    <CardContent
                      sx={{
                        padding: '12px',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                      }}
                    >
                      <Typography
                        variant="h6"
                        className="product-name"
                        gutterBottom
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          minHeight: "2.5em",
                          lineHeight: 1.3,
                          color: '#263238',
                          marginBottom: '8px',
                        }}
                      >
                        {product.name}
                      </Typography>

                      {product.brand && (
                        <Chip
                          label={product.brand}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: '22px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            marginBottom: '8px',
                            maxWidth: 'fit-content',
                          }}
                        />
                      )}

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '8px',
                          flexWrap: 'wrap',
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: '#1976d2',
                            fontSize: '1.1rem',
                            fontWeight: 700,
                          }}
                        >
                          ₹{product.price}
                        </Typography>
                        {product.discount > 0 && (
                          <Chip
                            label={`${product.discount}% OFF`}
                            color="error"
                            size="small"
                            sx={{
                              height: '20px',
                              fontSize: '0.65rem',
                              fontWeight: 700,
                            }}
                          />
                        )}
                      </Box>

                      {/* STOCK STATUS */}
                      <Box sx={{ marginBottom: '8px' }}>
                        {product.stockStatus && (
                          <Chip
                            label={product.stockStatus}
                            size="small"
                            sx={{
                              height: '22px',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              backgroundColor: product.isOutOfStock ? '#ffebee' : '#e8f5e9',
                              color: product.isOutOfStock ? '#d32f2f' : '#2e7d32',
                              border: `1px solid ${product.isOutOfStock ? '#ffcdd2' : '#c8e6c9'}`,
                              marginBottom: '4px',
                            }}
                          />
                        )}
                      </Box>

                      <Button
                        fullWidth
                        variant={isProductSelected(product._id) ? "contained" : "outlined"}
                        color={isProductSelected(product._id) ? "success" : "primary"}
                        startIcon={
                          isProductSelected(product._id) ? (
                            <FaCheckCircle />
                          ) : (
                            <MdAddCircle />
                          )
                        }
                        disabled={product.isOutOfStock}
                        sx={{
                          marginTop: 'auto',
                          padding: '8px 12px',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          transition: 'all 0.3s ease',
                          ...(product.isOutOfStock ? {
                            backgroundColor: '#e0e0e0',
                            color: '#9e9e9e',
                            cursor: 'not-allowed',
                            '&:hover': {
                              backgroundColor: '#e0e0e0',
                            },
                          } : isProductSelected(product._id) ? {
                            background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                            boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                            },
                          } : {
                            borderWidth: '2px',
                            '&:hover': {
                              borderWidth: '2px',
                              background: 'rgba(25, 118, 210, 0.04)',
                              transform: 'translateY(-2px)',
                            },
                          }),
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!product.isOutOfStock) {
                            toggleProductSelection(product);
                          }
                        }}
                      >
                        {product.isOutOfStock ? "Unavailable" : isProductSelected(product._id) ? "Selected" : "Select"}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Submit Button */}
        <Box
          className="card shadow border-0 p-4 mt-4"
          sx={{
            flexDirection: 'row',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            padding: '24px',
            borderRadius: '12px',
            backgroundColor: '#fff',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)'
            }
          }}
        >
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={loading || uploading}
            startIcon={
              loading || uploading ? <CircularProgress size={20} /> : <MdCloudUpload />
            }
            sx={{
              minWidth: '200px',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              padding: '12px 32px',
              fontSize: '1rem',
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.35)'
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            {loading || uploading ? "Updating..." : "Update Offer Banner"}
          </Button>

          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/homeOfferBanners")}
            disabled={loading || uploading}
            sx={{
              minWidth: '140px',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              padding: '12px 32px',
              fontSize: '1rem',
              borderWidth: '2px',
              borderColor: 'primary.main',
              color: 'primary.main',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderWidth: '2px',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                transform: 'translateY(-2px)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              },
              '&:disabled': {
                borderColor: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </form>
    </div>
  );
};

export default EditOfferBanner;
